import type {ReactElement} from 'react';
import React from 'react';
import {BaseImageRoundnessOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-image-roundness-option/base-image-roundness-option';
import {DEFAULT_CURVED_ROUNDESS, EdgeType} from '@PosterWhiteboard/classes/item-effects.class';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetKeyFromEffectsOfActiveSlideItem} from '@Hooks/poster-editor/useGetKeyFromEffectsOfActiveSlideItem';

interface ImageSlideRoundnessOptionProps {
  isInPanel?: boolean;
  panelId?: string;
}

export function ImageSlideRoundnessOption({isInPanel = false, panelId = ''}: ImageSlideRoundnessOptionProps): ReactElement {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const edgeThickness = useGetKeyFromEffectsOfActiveSlideItem('edgeThickness');
  const edgeType = useGetKeyFromEffectsOfActiveSlideItem('edgeType');

  if (!activeItem || !activeSlideUID) {
    throw new Error('Active item or active slide is not defined in slideshow item effects options');
  }

  if (!activeItem.isSlideshow()) {
    throw new Error(`Expected active item to be of type slideshow, got ${activeItem.gitype} instead`);
  }

  if (edgeThickness === undefined || edgeType === undefined) {
    throw new Error('Unable to find edge thickness or edge type for slideshow item');
  }

  const isCurvedApplied = (): boolean => {
    return edgeType === EdgeType.CURVED;
  };

  const onToggleCurved = async (): Promise<void> => {
    await activeItem.updateSlideFromObject(activeSlideUID, {
      effects: {
        edgeType: isCurvedApplied() ? EdgeType.NONE : EdgeType.CURVED,
      },
    });

    if (!isCurvedApplied()) {
      await activeItem.updateSlideFromObject(
        activeSlideUID,
        {
          effects: {
            edgeThickness: DEFAULT_CURVED_ROUNDESS,
          },
        },
        {undoable: false}
      );
    }
  };

  const onUpdateThickness = async (value: number, undoable = false): Promise<void> => {
    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        effects: {
          edgeThickness: value,
        },
      },
      {undoable}
    );
  };

  return (
    <BaseImageRoundnessOption
      isOpenedInPanel={isInPanel}
      panelId={panelId}
      isChecked={isCurvedApplied()}
      onToggleCurved={(): void => {
        void onToggleCurved();
      }}
      edgeThickness={edgeThickness}
      onUpdateThickness={(value, undoable): void => {
        void onUpdateThickness(value, undoable);
      }}
    />
  );
}
